/**
 * Générateur — Il modélise et résout des problèmes mettant en jeu la
 * divisibilité (engrenages, conjonction de phénomènes…).
 * Attendu ID : 207 | Niveau : 3ème | Domaine : Divisibilité (d10, O7)
 *
 * Source doc prof (NOMBRE ET CALCULS 3e, séance A5S1). Plan de cartes
 * auto-validé 2026-07-16 (journal TODO_EXERCISEUR_3E.md §6) :
 *
 *   « euclidienne » — retrouver diviseur ou reste (a = b×q + r, 1 trou)
 *   « reste »       — a = A×B + r avec r > B : réécrire (2 trous — l'Ex.2b)
 *   « tondeuse »    — quotient par excès en situation (bandes + distance)
 *   « devinette »   — divisible par x mais pas par y (UNIQUE par construction)
 *   « multiples »   — démonstrations 24k = 4 × 6k (trous numériques)
 *   « phare »       — conjonction de phénomènes : PPCM en secondes → minutes
 *   « engrenage »   — PPCM des dents → tours de chaque roue
 *   « partage »     — partages maximaux : PGCD (lots / mosaïque)
 *
 * Adaptation : les démonstrations rédigées deviennent des trous numériques
 * (menus du doc → saisie), le raisonnement reste porté par la structure.
 */

import {
    randomChoice, randInt, tex,
    makeTexteTrou,
} from '../../../utils';
import { pgcd, ppcm } from './divisibilite';

const ATTENDU_ID = 207;

const L_EUCLIDIENNE = 'Retrouver un terme d\'une division euclidienne';
const L_RESTE = 'Lire les restes dans une égalité a = b × q + r';
const L_TONDEUSE = 'Compter des bandes (quotient par excès)';
const L_DEVINETTE = 'Résoudre une devinette de divisibilité';
const L_MULTIPLES = 'Démontrer avec l\'écriture 24k d\'un multiple';
const L_PHARE = 'Prévoir une conjonction de phénomènes (PPCM)';
const L_ENGRENAGE = 'Étudier un engrenage (PPCM des dents)';
const L_PARTAGE = 'Faire des partages maximaux (PGCD)';

const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ═════════ « euclidienne » — retrouver diviseur ou reste ═════════

const genEuclidienneTrous = () => {
    if (randInt(0, 1)) {
        // Retrouver le DIVISEUR : a = n×q + r avec r < n → n = (a − r)/q unique.
        const n = randInt(7, 25); const q = randInt(5, 12); const r = randInt(1, n - 1);
        const a = n * q + r;
        const phrase = `Le diviseur est n = {{${n}}}.`;
        const payload = makeTexteTrou(phrase,
            `<p>Dans la division euclidienne de ${a} par n, le quotient est ${q} et le reste est ${r}.</p>`
            + '<p><b>Calcule n.</b></p>',
            { ...opts(L_EUCLIDIENNE), mode: 'saisie' });
        payload._meta.params = { forme: 'diviseur', a, q, r, n };
        return payload;
    }
    // Retrouver le RESTE : a = d×q + n avec 0 ≤ n < d.
    const d = randInt(11, 29); const q = randInt(5, 14); const n = randInt(1, d - 1);
    const a = d * q + n;
    const phrase = `Le reste est n = {{${n}}}.`;
    const payload = makeTexteTrou(phrase,
        `<p>Dans la division euclidienne de ${a} par ${d}, le quotient est ${q} et le reste est n.</p>`
        + '<p><b>Calcule n.</b></p>',
        { ...opts(L_EUCLIDIENNE), mode: 'saisie' });
    payload._meta.params = { forme: 'reste', a, d, q, n };
    return payload;
};

// ═════════ « reste » — a = A×B + r avec B < r < 2B (l'Ex.2b) ═════════

const genResteTrous = () => {
    let A; let B; let r;
    for (let essai = 0; essai < 60; essai++) {
        B = randInt(20, 60);
        A = B * randInt(3, 7) + randInt(1, 9);           // A > 2B garanti (≥ 3B)
        r = B + randInt(1, B - 1);                        // B < r < 2B
        if (r < A && r - B >= 1) break;
        r = 0;
    }
    if (!r) { A = 219; B = 43; r = 45; }                  // repli (doc Ex.2)
    const a = A * B + r;
    const affA = String(a).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const phrase = `Reste de la division par ${A} : {{${r}}} ; reste de la division par ${B} : {{${r - B}}}.`;
    const payload = makeTexteTrou(phrase,
        `<p>On donne l'égalité ${tex(`${affA.replace(/ | /g, '\\,')} = ${A} \\times ${B} + ${r}`)}.</p>`
        // Le « Attention : un reste est toujours plus petit que le diviseur »
        // désamorçait exactement le piège de l'exercice (r > B).
        + `<p><b>Sans poser de division, donne le reste de la division euclidienne de ${affA} par ${A}, `
        + `puis par ${B}.</b></p>`,
        { ...opts(L_RESTE), mode: 'saisie' });
    payload._meta.params = { a, A, B, r };
    return payload;
};

// ═════════ « tondeuse » — quotient par excès ═════════

const genTondeuseTrous = () => {
    let w; let larg; let long; let bandes; let dist;
    for (let essai = 0; essai < 60; essai++) {
        w = randomChoice([150, 180, 216, 240]);          // largeur de coupe en cm
        larg = randInt(40, 80);                           // largeur du terrain en m
        long = randInt(85, 120);                          // longueur en m
        if ((larg * 100) % w === 0) continue;             // division NON exacte → « par excès »
        bandes = Math.ceil((larg * 100) / w);
        dist = bandes * long;
        if (bandes >= 20 && bandes <= 55 && dist <= 6500) break;
        bandes = 0;
    }
    if (!bandes) { w = 216; larg = 69; long = 97; bandes = 32; dist = 3104; } // repli (doc Akim)
    const engin = randomChoice(['Une tondeuse', 'Un robot de tonte']);
    const phrase = `Nombre de bandes : {{${bandes}}} ; distance parcourue : {{${dist}}} m.`;
    const payload = makeTexteTrou(phrase,
        `<p>${engin} coupe sur une largeur de ${w} cm. Le terrain rectangulaire mesure `
        + `${long} m de longueur et ${larg} m de largeur. On tond par bandes parallèles à la longueur.</p>`
        + '<p>La dernière bande peut chevaucher la précédente.</p>'
        + '<p><b>Combien de bandes faut-il au minimum, et quelle distance est parcourue ?</b></p>',
        { ...opts(L_TONDEUSE), mode: 'saisie' });
    payload._meta.params = { w, larg, long, bandes, dist };
    return payload;
};

// ═════════ « devinette » — unique par construction ═════════

const genDevinetteTrous = () => {
    let t; let lo; let hi;
    for (let essai = 0; essai < 120; essai++) {
        t = 6 * randInt(20, 400);
        if (t % 4 === 0 || t % 9 === 0) continue;
        lo = t - randInt(4, 8); hi = t + randInt(4, 8);
        // Unicité : aucun autre multiple de 6 valide dans [lo, hi].
        let seul = true;
        for (let x = lo; x <= hi; x++) {
            if (x !== t && x % 6 === 0 && x % 4 !== 0 && x % 9 !== 0) { seul = false; break; }
        }
        if (seul) break;
        t = 0;
    }
    if (!t) { t = 2346; lo = 2336; hi = 2352; }           // repli (doc Ex.7)
    const phrase = `Je suis {{${t}}}.`;
    const payload = makeTexteTrou(phrase,
        `<p>Je suis un nombre entier compris entre ${lo} et ${hi}. Je suis divisible par 2 et par 3, `
        + 'mais ni par 4 ni par 9.</p><p><b>Qui suis-je ?</b></p>',
        { ...opts(L_DEVINETTE), mode: 'saisie' });
    payload._meta.params = { t, lo, hi };
    return payload;
};

// ═════════ « multiples » — démonstrations 24k = 4 × 6k ═════════

const genMultiplesTrous = () => {
    let m; let d;
    for (let essai = 0; essai < 40; essai++) {
        m = randomChoice([12, 18, 24, 30, 36, 40, 45]);
        const divs = [];
        for (let x = 2; x <= m / 2; x++) if (m % x === 0) divs.push(x);
        d = randomChoice(divs);
        if (d !== m && m / d >= 2) break;
        d = 0;
    }
    if (!d) { m = 24; d = 4; }
    const e = m / d;
    // La propriété visée est NOMMÉE (« multiple de d ») — « le multiple d'un
    // autre nombre » était ambigu — et le facteur ek n'est plus pré-rempli.
    const phrase = `$${m}k = ${d} \\times {{${e}k}}$\n`
        + `$${m}k + ${m}k' = {{${m}}} \\times \\left(k + k'\\right)$`;
    const payload = makeTexteTrou(phrase,
        `<p>Un multiple de ${m} s'écrit ${tex(`${m}k`)}, avec k entier.</p>`
        + `<p><b>Complète les égalités</b>, en écrivant le facteur manquant sous la forme « ${e}k ». `
        + `La première démontre qu'un multiple de ${m} est aussi un multiple de ${d} ; `
        + `la seconde, que la somme de deux multiples de ${m} est encore un multiple de ${m}.</p>`,
        { ...opts(L_MULTIPLES), mode: 'saisie' });
    payload._meta.params = { m, d, e };
    return payload;
};

// ═════════ « phare » — conjonction de phénomènes (PPCM) ═════════

const genPhareTrous = () => {
    let T1; let T2; let L;
    for (let essai = 0; essai < 80; essai++) {
        T1 = randomChoice([60, 90, 120, 180, 150]);
        T2 = randomChoice([28, 56, 84, 112, 24, 36, 48]);
        L = ppcm(T1, T2);
        if (T1 !== T2 && L % 60 === 0 && L >= 240 && L <= 3600 && L !== T1 && L !== T2) break;
        L = 0;
    }
    if (!L) { T1 = 180; T2 = 56; L = 2520; }              // repli (doc phare)
    const minutes = L / 60;
    const habillage = randomChoice([
        [`La lumière blanche d'un phare clignote toutes les ${T1} s, la verte toutes les ${T2} s.`, 'clignotent-elles'],
        [`Un feu clignote toutes les ${T1} s, un autre toutes les ${T2} s.`, 'clignotent-ils'],
        [`Une fontaine jaillit toutes les ${T1} s, une autre toutes les ${T2} s.`, 'jaillissent-elles'],
    ]);
    const phrase = `Prochaine conjonction : dans {{${L}}} s, c'est-à-dire {{${minutes}}} min après minuit.`;
    const payload = makeTexteTrou(phrase,
        `<p>${habillage[0]} À minuit pile, les deux se déclenchent ensemble.</p>`
        // « Utilise les décompositions en facteurs premiers » livrait la méthode.
        + `<p><b>Au bout de combien de secondes ${habillage[1]} à nouveau ensemble ?</b> `
        + 'Donne aussi la durée en minutes.</p>',
        { ...opts(L_PHARE), mode: 'saisie' });
    payload._meta.params = { T1, T2, L, minutes };
    return payload;
};

// ═════════ « engrenage » — PPCM des dents ═════════

const genEngrenageTrous = () => {
    let d1; let d2; let L; let t1; let t2;
    for (let essai = 0; essai < 80; essai++) {
        d1 = randomChoice([12, 16, 18, 20, 24, 30, 36]);
        d2 = randomChoice([12, 16, 18, 20, 24, 30, 36]);
        if (d1 === d2) continue;
        L = ppcm(d1, d2);
        t1 = L / d1; t2 = L / d2;
        if (t1 >= 2 && t2 >= 2 && t1 <= 12 && t2 <= 12 && L <= 200 && t1 !== t2) break;
        L = 0;
    }
    if (!L) { d1 = 24; d2 = 16; L = 48; t1 = 2; t2 = 3; } // repli (doc engrenage)
    const phrase = `Il faut {{${L}}} dents entraînées ; la roue de ${d1} dents fait {{${t1}}} tours et celle de ${d2} dents fait {{${t2}}} tours.`;
    const payload = makeTexteTrou(phrase,
        `<p>Un engrenage comprend deux roues dentées de ${d1} et ${d2} dents. Au départ, deux dents `
        + 'marquées sont en contact.</p>'
        + '<p><b>Après combien de dents entraînées les deux marques se retrouvent-elles en contact, '
        + 'et combien de tours fait alors chaque roue ?</b></p>',
        { ...opts(L_ENGRENAGE), mode: 'saisie' });
    payload._meta.params = { d1, d2, L, t1, t2 };
    return payload;
};

// ═════════ « partage » — partages maximaux (PGCD) ═════════

const genPartageTrous = () => {
    if (randInt(0, 2) < 2) {
        // Lots identiques : g = PGCD, constitution a + b.
        let g; let a; let b;
        for (let essai = 0; essai < 60; essai++) {
            g = randInt(12, 60);
            a = randInt(3, 12); b = randInt(3, 12);
            if (pgcd(a, b) === 1 && a !== b && g !== a && g !== b && g * a <= 1500 && g * b <= 1500) break;
            g = 0;
        }
        if (!g) { g = 45; a = 10; b = 7; }                // repli (doc tartelettes)
        const A = g * a; const B = g * b;
        const habillage = randomChoice([
            [`Un pâtissier dispose de ${A} morceaux de pomme et ${B} framboises pour garnir des tartelettes identiques, en utilisant tout.`, 'tartelettes', 'morceaux de pomme', 'framboises', 'chacune'],
            [`Une fleuriste dispose de ${A} tulipes et ${B} œillets pour composer des bouquets identiques, en utilisant tout.`, 'bouquets', 'tulipes', 'œillets', 'chacun'],
            [`Un traiteur dispose de ${A} canapés et ${B} macarons pour préparer des plateaux identiques, en utilisant tout.`, 'plateaux', 'canapés', 'macarons', 'chacun'],
        ]);
        const phrase = `Au maximum : {{${g}}} ${habillage[1]}, avec {{${a}}} ${habillage[2]} et {{${b}}} ${habillage[3]} ${habillage[4]}.`;
        const payload = makeTexteTrou(phrase,
            `<p>${habillage[0]}</p>`
            + `<p><b>Combien de ${habillage[1]} au maximum, et quelle est leur composition ?</b></p>`,
            { ...opts(L_PARTAGE), mode: 'saisie' });
        payload._meta.params = { forme: 'lots', g, a, b };
        return payload;
    }
    // Mosaïque : carreaux carrés maximaux sur un mur H × W.
    let g; let a; let b;
    for (let essai = 0; essai < 60; essai++) {
        g = randomChoice([6, 9, 12, 15, 18]);
        a = randInt(7, 30); b = randInt(7, 30);
        if (pgcd(a, b) === 1 && a !== b && g * a <= 400 && g * b <= 400 && a * b <= 400) break;
        g = 0;
    }
    if (!g) { g = 9; a = 12; b = 25; }                    // repli (doc mosaïque)
    const H = g * a; const W = g * b;
    const phrase = `Côté maximal : {{${g}}} cm ; nombre de carreaux : {{${a * b}}}.`;
    const payload = makeTexteTrou(phrase,
        `<p>On veut recouvrir un mur de ${tex(`${H} \\times ${W}`)} cm avec des carreaux CARRÉS `
        + 'identiques, sans découpe.</p>'
        + '<p><b>Quelle est la dimension maximale des carreaux, et combien en faut-il alors ?</b></p>',
        { ...opts(L_PARTAGE), mode: 'saisie' });
    payload._meta.params = { forme: 'mosaique', g, a, b };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    euclidienne_texte_trou_saisie: genEuclidienneTrous,
    reste_texte_trou_saisie: genResteTrous,
    tondeuse_texte_trou_saisie: genTondeuseTrous,
    devinette_texte_trou_saisie: genDevinetteTrous,
    multiples_texte_trou_saisie: genMultiplesTrous,
    phare_texte_trou_saisie: genPhareTrous,
    engrenage_texte_trou_saisie: genEngrenageTrous,
    partage_texte_trou_saisie: genPartageTrous,
};

const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'euclidienne', label: L_EUCLIDIENNE, formatsList: buildFormats('euclidienne') },
    { id: 'reste', label: L_RESTE, formatsList: buildFormats('reste') },
    { id: 'tondeuse', label: L_TONDEUSE, formatsList: buildFormats('tondeuse') },
    { id: 'devinette', label: L_DEVINETTE, formatsList: buildFormats('devinette') },
    { id: 'multiples', label: L_MULTIPLES, formatsList: buildFormats('multiples') },
    { id: 'phare', label: L_PHARE, formatsList: buildFormats('phare') },
    { id: 'engrenage', label: L_ENGRENAGE, formatsList: buildFormats('engrenage') },
    { id: 'partage', label: L_PARTAGE, formatsList: buildFormats('partage') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
